import * as React from 'react';

/** Material UI Packages. */
import { Box, MenuItem, Select, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';

/** Internal Components. */
import { operationRangeDaySlot } from 'shared/ui/OperationPickersDay';

/**
 * Lets the operator pick the search period: which date to go by (ETD, ETA …) and from when
 * to when. Shipment COMPLETE and Rate Management use the same control so it works the same
 * on both pages. The caller keeps the values and the action buttons.
 *
 * @param {Array<{value: string, label: string}>} fields - Date columns the window can apply to.
 * @param {string} field - Selected date column.
 * @param {function} onFieldChange - Called with the new column value.
 * @param {[dayjs, dayjs]} range - Picker value (start, end).
 * @param {function} onRangeChange - Called with the new picker value.
 * @param {boolean} error - Draws the range fields in red (e.g. the draft spans more than a year).
 * @param {node} hint - Optional line under the fields (a rule reminder or the error reason).
 * @param {string} title - Title line; default is the Shipment page's.
 */
export default function DateWindowFields({
    fields,
    field,
    onFieldChange,
    range,
    onRangeChange,
    error = false,
    hint = null,
    title = 'Date range (up to 1 year)',
}) {
    return (
        <>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#003a52' }}>{title}</Typography>
            <Select
                size="small"
                value={field}
                onChange={(event) => onFieldChange(event.target.value)}
                sx={{ height: '32px', fontSize: 12 }}
            >
                {fields.map(({ value, label }) => (
                    <MenuItem key={value} value={value} sx={{ fontSize: 12 }}>
                        {label}
                    </MenuItem>
                ))}
            </Select>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateRangePicker
                    value={range}
                    onChange={onRangeChange}
                    slots={operationRangeDaySlot}
                    slotProps={{
                        textField: { InputLabelProps: { shrink: true }, error },
                    }}
                    localeText={{ start: '', end: '' }}
                    sx={{ '& .MuiInputBase-input': { px: 1, py: 0, height: '32px', fontSize: 12 } }}
                />
            </LocalizationProvider>
            {hint ? (
                <Box sx={{ fontSize: 11, color: error ? '#d32f2f' : '#5a6b74', mt: -0.5 }}>{hint}</Box>
            ) : null}
        </>
    );
}
